import React from "react";
import { motion } from "framer-motion";
import { IMAGES } from "@/lib/assets";
import { Award, Hammer, GraduationCap, Recycle, Target, Users as UsersIcon, LineChart } from "lucide-react";

/**
 * FounderCard — a single founder column.  Two of these sit side-by-side
 * in a 50/50 grid on `/founder` (Rob left, William right).  Each card
 * owns its own image frame, kicker, headline, bio paragraphs, operating-
 * philosophy block, and stats grid — so the two halves feel balanced.
 */
const FounderCard = ({ founder, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.7, delay, ease: [0.19, 1, 0.22, 1] }}
    data-testid={`founder-card-${founder.key}`}
    className="flex flex-col"
  >
    {/* Portrait */}
    <div className="card-industrial corner-tick overflow-hidden relative">
      <img
        src={founder.image}
        alt={founder.alt}
        className="w-full h-auto aspect-square object-cover bg-[#0A0F1C]"
      />
      <div className="absolute top-4 left-4 w-14 h-14 border-t border-l border-nex-gold/60 pointer-events-none" />
      <div className="absolute bottom-4 right-4 w-14 h-14 border-b border-r border-nex-gold/60 pointer-events-none" />
    </div>
    <div className="mt-4 mono-body text-[11px] text-nex-chrome text-center tracking-wider">
      {founder.caption}
    </div>

    {/* Kicker + headline */}
    <div className="flex items-center gap-4 mt-8 mb-6">
      <div className="h-px w-8 bg-nex-gold" />
      <span className="mono-label">{founder.kicker}</span>
    </div>
    <h2 className="serif-head text-3xl md:text-4xl xl:text-5xl mb-6 leading-tight">
      {founder.headline}
    </h2>

    {/* Bio paragraphs */}
    <div className="flex flex-col gap-5">
      {founder.paragraphs.map((p, i) => (
        <p key={i} className="text-nex-chrome text-base lg:text-lg leading-relaxed">
          {p}
        </p>
      ))}
    </div>

    {/* Operating philosophy quote */}
    <div className="card-industrial p-6 mt-8">
      <div className="mono-label mb-3">{founder.philosophyLabel}</div>
      <blockquote className="serif-head text-xl md:text-2xl italic text-nex-silver leading-snug">
        {founder.quote}
      </blockquote>
      <div className="grid grid-cols-3 gap-3 mt-6 pt-6 border-t border-white/10">
        {founder.pillars.map(({ icon: Icon, k }) => (
          <div key={k} className="flex items-center gap-2">
            <Icon size={16} className="text-nex-gold shrink-0" />
            <span className="serif-head text-base md:text-lg text-nex-silver">{k}</span>
          </div>
        ))}
      </div>
    </div>

    {/* Stats grid */}
    <div className="grid grid-cols-2 gap-3 mt-6">
      {founder.stats.map(([k, v], i) => (
        <div key={i} className="border border-white/10 p-4">
          <Award size={14} className="text-nex-gold mb-2" />
          <div className="serif-head text-lg md:text-xl text-nex-silver">{k}</div>
          <div className="mono-body text-[10px] mt-0.5">{v}</div>
        </div>
      ))}
    </div>
  </motion.div>
);

const FOUNDERS = {
  rob: {
    key: "rob",
    image: IMAGES.founderRob,
    alt: "Rob Hatch — NEX · Partner & Strategic Growth Leader",
    caption: "Nexengines · Partner & Strategic Growth Leader",
    kicker: "// The Strategist",
    headline: (
      <>
        A <span className="italic text-nex-gold">proven revenue leader</span> — driving scalable growth by aligning customer strategy with cutting-edge digital infrastructure.
      </>
    ),
    paragraphs: [
      "Rob Hatch brings extensive expertise in sales development, client relationship management, and high-performance team leadership. Built on a foundation of strategic rigor, his approach ensures that market expansion is driven by clarity, execution, and long-term sustainability.",
      <>
        As Partner and Strategic Growth Leader at Nexengines, <span className="text-nex-silver font-semibold">Rob specializes in identifying high-value growth opportunities</span> and pairing customer-focused strategies with innovative platform solutions. His leadership consistently bridges complex technology with market execution to drive sustained revenue growth.
      </>,
      <>
        Today, Rob drives the <span className="text-nex-silver">strategic acquisition and partnership framework</span> across the NEX ecosystem. He works alongside executive leadership to expand enterprise reach, build high-impact client alliances, and ensure NEX serves as the definitive growth engine for business transformation.
      </>,
    ],
    philosophyLabel: "// Growth Doctrine",
    quote: (
      <>
        "Durable growth lives at the intersection of <span className="text-nex-gold">clarity, execution, and relationships that compound.</span>"
      </>
    ),
    pillars: [
      { icon: Target,    k: "Align" },
      { icon: UsersIcon, k: "Partner" },
      { icon: LineChart, k: "Scale" },
    ],
    stats: [
      ["20+ yrs",     "revenue leadership"],
      ["Partner",     "Strategic Growth Lead"],
      ["Enterprise",  "client alliances"],
      ["Full-funnel", "sales × platform"],
    ],
  },

  william: {
    key: "william",
    image: IMAGES.founder,
    alt: "William O. Smith — NEX · Custom Business Development Initiative",
    caption: "LIFE LLC · Life Foundation · Founder & President",
    kicker: "// The Architect",
    headline: (
      <>
        An <span className="italic text-nex-gold">Air Force mechanic</span> turned social entrepreneur — engineering business systems the same way he engineered aircraft.
      </>
    ),
    paragraphs: [
      "William O. Smith served in the United States Air Force as an aircraft mechanic, where precision, meticulous planning, and complex problem-solving were non-negotiable. Those disciplines became the operating system of everything he has built since.",
      <>
        In 2004, William founded a car dealership and <span className="text-nex-silver font-semibold">quadrupled its value in four years.</span> He later became a licensed mortgage broker, developing deep credit-underwriting expertise. Through Blue-Collar Fund and Management LLC, he secured federal grants and designed literacy programs that <span className="text-nex-silver font-semibold">empowered 450+ families</span> toward financial stability and homeownership.
      </>,
      <>
        Today, William leads the institutional expansion of the "Life Navigation" ecosystem — including the <span className="text-nex-silver">$4.5M Kingston/Hilton undertaking</span> — and directs the build-out of a 6-acre multi-service educational and vocational site. NEX is the digital infrastructure arm of this life's work.
      </>,
    ],
    philosophyLabel: "// Operating Philosophy",
    quote: (
      <>
        "True transformation occurs at the intersection of <span className="text-nex-gold">education, work, and divine guidance.</span>"
      </>
    ),
    pillars: [
      { icon: Hammer,         k: "Build" },
      { icon: GraduationCap,  k: "Educate" },
      { icon: Recycle,        k: "Recycle" },
    ],
    stats: [
      ["USAF",    "aircraft mechanic"],
      ["4x",      "dealership value in 4 yrs"],
      ["450+",    "families empowered"],
      ["$4.5M",   "Kingston/Hilton project"],
    ],
  },
};

const Founder = () => (
  <section
    id="founder"
    data-testid="founder-section"
    className="relative py-28 lg:py-36 bg-[#0A0F1C]"
  >
    <div className="w-full mx-auto px-6 md:px-10 lg:px-12 xl:px-20 2xl:px-28 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
      <FounderCard founder={FOUNDERS.rob}     delay={0}    />
      <FounderCard founder={FOUNDERS.william} delay={0.1}  />
    </div>
  </section>
);

export default Founder;
